import type { TileImageSize } from '@/plugins/tiles/mesh/decodeTileImage'

/** 仅传递独占字节、尺寸和代际；不在线程间传递 AbortSignal 或 Three 对象。 */
export interface TileImageWorkerRequest {
  readonly type: 'decode'
  readonly id: number
  readonly generation: number
  readonly data: ArrayBuffer
  readonly mimeType: string
  readonly target: TileImageSize
}

export type TileImageWorkerResponse =
  | {
      readonly type: 'result'
      readonly id: number
      readonly generation: number
      readonly bitmap: ImageBitmap
      readonly processingMs: number
    }
  | {
      readonly type: 'error'
      readonly id: number
      readonly generation: number
      readonly code: 'unsupported-capability' | 'invalid-resource'
      readonly message: string
    }

export interface TileImageDecodeRequest {
  /** 借用输入，服务只复制图片范围；不分离调用者的 GLB 或几何缓冲。 */
  readonly data: ArrayBuffer | Uint8Array<ArrayBuffer>
  readonly mimeType: string
  readonly sourceSize: TileImageSize
  readonly target: TileImageSize
  readonly signal: AbortSignal
}

export interface TileImageServiceStatus {
  readonly path: 'worker'
  readonly queued: number
  readonly active: number
  readonly activePeakBytes: number
  readonly workerCount: number
  readonly failures: number
  readonly closed: boolean
  /** 已回执任务的 Worker 内部加工墙钟摘要；未启用或无有效样本时未知。 */
  readonly workerProcessing?: Readonly<{ count: number; totalMs: number; maxMs: number }> | null
}

export interface ITileImageDecodeService {
  /** 活动任务取消后仍到真实完成才结算；迟到位图不会交给调用者。 */
  decode(request: TileImageDecodeRequest): Promise<ImageBitmap>
  getStatus(): TileImageServiceStatus
  dispose(): void
}

export interface TileImageDecodeServiceOptions {
  /** 开启常量空间耗时摘要，默认关闭；不包含排队或主线程传输等待。 */
  readonly diagnostics?: boolean
  readonly workerUrl?: string | URL
  readonly workerFactory?: () => Worker
  /** 图片执行槽，默认 2，范围 1..16。 */
  readonly concurrency?: number
  /** 原图加工的共享软执行容量；候选仍须先满足运行时 CPU/GPU 硬预算。 */
  readonly maxPeakBytes?: number
  /** 等待队列上限，默认 256；等待者不占执行槽。 */
  readonly maxQueued?: number
  readonly timeoutMs?: number
  /** 故障 Worker 的最多替换次数，默认 2；不会重放失败任务。 */
  readonly maxRestarts?: number
}

/** 输入独占副本、源像素/兼容画布、目标位图/传输重叠的保守执行峰值。 */
export function tileImagePeakBytes(request: TileImageDecodeRequest): number {
  return (
    request.data.byteLength * 2 +
    request.sourceSize.width * request.sourceSize.height * 8 +
    request.target.width * request.target.height * 8
  )
}
